<template>
    <div class="panel-voice-selected">
        <!-- necessary class to select this particular icon -->
        <base-icon
            class="panel-voice-selected__close"
            name="close"
            @onClick="close"
        ></base-icon>

        <voice-badge
            :id="voice.id"
            :name="voice.name"
            :favourite-always-visible="voice.subscriberOnly"
            :has-favourite="voice.subscriberOnly"
            :icon="get_voiceIcon(voice.voiceId)"
            isSelected
            urlBackground
        >
            <template #icon>
                <base-icon
                    name="twitch-prime"
                    size="normal"
                    color="active"
                >
                </base-icon>
            </template>
        </voice-badge>

        <base-button
            :is-type="['simple']"
            @onClick="previewVoice"
        >
            <template slot="label">
                {{ $t('twitch.button.previewVoice') }}

                <audio ref="previewUrl">
                    <source
                        :src="get_voicePreview(voice.voiceId)"
                        type="audio/mpeg"
                    >
                    {{ $t('twitch.browserNoSupportAudio') }}
                </audio>
            </template>
        </base-button>

        <div
            class="panel-voice-selected__subscriber-only"
            v-if="voice.subscriberOnly ? viewerSubscriptionStatus !== null : true"
        >
            <base-range-counter
                :counter-value="time"
                :minimum-reached="time <= panelMinimumTime()"
                :maximum-reached="time >= maximumTime()"
                @increase="increaseTime()"
                @decrease="decreaseTime()"
            >
                <template #counterMeasure>{{ $t('twitch.settings.sec') }}</template>
            </base-range-counter>

            <panel-price-box :price="price ? price : null">
                <template #priceIcon>
                    <base-icon
                        name="bits"
                        size="small"
                        color="light"
                    ></base-icon>
                </template>
                <template #priceMeasure>{{ price ? 'Bits' : $t('twitch.globalMsg.free') }}</template>
            </panel-price-box>

            <base-button
                v-if="!get_isPurchasedVoice(voice.id)"
                class="panel-voice-selected__change-voice"
                :is-type="['simple', 'install']"
                @onClick="changeVoice"
            >
                <template slot="label">
                    {{ $t('twitch.button.changeVoice') }}
                </template>
            </base-button>
            <p
                v-else
                class="panel-voice-selected__expiration"
            >
                {{ $t('twitch.panel.expiration') }}

                <expiration-time :voice-unique-id="voice.id"></expiration-time>
            </p>
        </div>

        <div
            class="panel-voice-selected__not-subscribed"
            v-else
        >
            <base-icon
                name="twitch-prime"
                size="normal"
                color="active"
            >
            </base-icon>

            <h2>{{ $t('twitch.onlySubscribers') }}</h2>

            <p>
                {{ $t('twitch.subscribeTo') }} <span>{{ twitchUserName }}</span> {{ $t('twitch.subscribeToUnlock') }}
            </p>
        </div>

    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import BaseButton from '@RepoComponent/BaseButton/BaseButton';
import ExpirationTime from '@Components/ExpirationTime/ExpirationTime';
import BaseRangeCounter from '@Components/BaseRangeCounter/BaseRangeCounter';
import PanelPriceBox from '@Components/PanelPriceBox/PanelPriceBox';

export default {
    name: 'PanelVoiceSelected',

    components: {
        VoiceBadge: () => import(/* webpackChunkName: "VoiceBadge" */ '@RepoComponent/VoiceBadge/VoiceBadge'),
        BaseIcon: () => import(/* webpackChunkName: "BaseIcon" */ '@RepoComponent/BaseIcon/BaseIcon'),
        ExpirationTime,
        BaseButton,
        BaseRangeCounter,
        PanelPriceBox,
    },

    data() {
        return {
            time: 5,
            price: 0,
            twitchUserName: 'streamer',
        };
    },

    computed: {
        ...mapGetters({
            get_voicePreview: 'panel/get_voicePreview',
            get_voiceIcon: 'panel/get_voiceIcon',
            get_panelVoiceSelected: 'panel/get_panelVoiceSelected',
            get_isPurchasedVoice: 'panel/get_isPurchasedVoice',
            twitchClientId: 'global/get_twitchClientId',
            twitchAuthToken: 'global/get_twitchAuthToken',
            twitchChannelId: 'global/get_twitchChannelId',
            voiceSelectedPrice: 'panel/get_voiceSelectedPrice',
        }),

        ...mapState({
            globalStore: state => state.global,
            twitchStore: state => state.twitchSettings,
        }),

        voice() {
            return this.get_panelVoiceSelected(this.$route.params.id);
        },

        voicePrice() {
            return this.voice.price;
        },

        viewerSubscriptionStatus() {
            return this.globalStore.viewerSubscriptionStatus;
        },

    },

    watch: {
        voice(voice) {
            if (Object.keys(voice).length === 0) {
                this.$router.push({ name: 'VoicesList' });
            }
        },
    },

    mounted() {
        this.price = this.voicePrice;
        this.time = this.panelMinimumTime();

        this.axn_viewerSubscriptionStatus(this.$twitch.viewer.subscriptionStatus);
    },

    methods: {
        ...mapActions({
            axn_viewerSubscriptionStatus: 'global/axn_viewerSubscriptionStatus',
        }),

        ...mapGetters({
            panelMinimumTime: 'panel/get_panelMinimumTime',
            maximumTime: 'panel/get_maximumTime',
        }),

        close() {
            this.$router.push({ name: 'VoicesList' });
        },

        previewVoice() {
            this.$refs.previewUrl.play();
        },

        changeVoice() {
            this.$router.push({
                name: 'PanelLoader',
                params: {
                    id: this.voice.id,
                    time: this.time,
                    price: this.price,
                },
            });
        },

        higherThanMaximum() {
            return this.time + this.panelMinimumTime() > this.maximumTime();
        },

        lessThanMinimum() {
            return this.time - this.panelMinimumTime() < this.panelMinimumTime();
        },

        setTimeIncreaseValue() {
            if(this.higherThanMaximum()) {
                return this.maximumTime();
            }

            return this.time + this.panelMinimumTime();
        },

        setTimeDecreaseValue() {
            if(this.lessThanMinimum()) {
                return this.panelMinimumTime();
            }

            return this.time - this.panelMinimumTime();
        },

        increaseTime() {
            this.time = this.setTimeIncreaseValue();

            this.price = this.voiceSelectedPrice(this.$route.params.id, this.time);
        },

        decreaseTime() {
            this.time = this.setTimeDecreaseValue();

            this.price = this.voiceSelectedPrice(this.$route.params.id, this.time);
        },

    },
};
</script>

<style src="./PanelVoiceSelected.scss" lang="scss" scoped></style>
